import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import * as Sentry from "@sentry/react"

import { AdminLayout } from '@/components/layout/admin-layout'
import { AppLayout } from '@/components/layout/app-layout'
import { TrainerLayout } from '@/components/layout/trainer-layout'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { isSupabaseConfigured } from '@/lib/supabase'
import { AdminBookingsPage } from '@/pages/admin/admin-bookings-page'
import { AdminCoursesPage } from '@/pages/admin/admin-courses-page'
import { AdminCustomerUsersPage } from '@/pages/admin/admin-customer-users-page'
import { AdminDiscountsPage } from '@/pages/admin/admin-discounts-page'
import { AdminReportsPage } from '@/pages/admin/admin-reports-page'
import { AdminResourcesPage } from '@/pages/admin/admin-resources-page'
import { AdminSettingsPage } from '@/pages/admin/admin-settings-page'
import { AdminSessionsPage } from '@/pages/admin/admin-sessions-page'
import { AdminNotificationsPage } from '@/pages/admin/admin-notifications-page'
import { AdminStaffUsersPage } from '@/pages/admin/admin-staff-users-page'
import { AdminUsersPage } from '@/pages/admin/admin-users-page'
import { AdminVouchersPage } from '@/pages/admin/admin-vouchers-page'
import { MemberAboPage } from '@/pages/member/member-abo-page'
import { MemberCalendarPage } from '@/pages/member/member-calendar-page'
import { MemberCoursesPage } from '@/pages/member/member-courses-page'
import { MemberDashboardPage } from '@/pages/member/member-dashboard-page'
import { CourseDetailPage } from '@/pages/public/course-detail-page'
import { TrainerDetailPage } from '@/pages/public/trainer-detail-page'
import { CoursesPage } from '@/pages/public/courses-page'
import {
  CourseRouteResolver,
  LegacyCourseBookingRedirect,
  LegacyCourseDetailRedirect,
} from '@/pages/public/legacy-course-booking-redirect'
import { LoginPage } from '@/pages/public/login-page'
import { ForgotPasswordPage } from '@/pages/public/forgot-password-page'
import { ResetPasswordPage } from '@/pages/public/reset-password-page'
import { NotFoundPage } from '@/pages/public/not-found-page'
import { RegisterPage } from '@/pages/public/register-page'
import { UserDetailPage } from '@/pages/shared/user-detail-page'
import { TrainerMyCoursesPage } from '@/pages/trainer/trainer-my-courses-page'
import { TrainerSessionsPage } from '@/pages/trainer/trainer-sessions-page'

const queryClient = new QueryClient()

function EnvironmentWarning() {
  if (isSupabaseConfigured) {
    return null
  }

  return (
    <div className="fixed left-1/2 top-3 z-[60] -translate-x-1/2 flex flex-col gap-2 items-center px-4">
      <Alert variant="destructive">
        <AlertDescription>
          Supabase ENV fehlt. Setze `VITE_SUPABASE_URL` und `VITE_SB_PUBLISHABLE_KEY` in deiner
          `.env.local`.
        </AlertDescription>
      </Alert>

    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <EnvironmentWarning />
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/registrieren" element={<RegisterPage />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
            <Route path="/reset-passwort" element={<ResetPasswordPage />} />

            <Route element={<AppLayout />}>
              <Route path="/kurse" element={<CoursesPage />} />
              <Route path="/kurse/:id" element={<CourseRouteResolver />} />
              <Route path="/kurse/:courseId/termine/:sessionId" element={<CourseDetailPage />} />
              <Route path="/kurse/kurs/:courseId" element={<LegacyCourseDetailRedirect />} />
              <Route path="/kurse/kurs/:courseId/termine/:sessionId" element={<LegacyCourseDetailRedirect />} />
              <Route path="/buchen/:sessionId" element={<LegacyCourseBookingRedirect />} />
              {/* Member routes */}
              <Route path="/dashboard" element={<MemberDashboardPage />} />
              <Route path="/abo" element={<MemberAboPage />} />
              <Route path="/meine-kurse" element={<MemberCoursesPage />} />
              <Route path="/kalender" element={<MemberCalendarPage />} />
            </Route>

            <Route path="/trainer" element={<TrainerLayout />}>
              <Route index element={<Navigate to="/trainer/meine-kurse" replace />} />
              <Route path="meine-kurse" element={<TrainerMyCoursesPage />} />
              <Route path="sessions" element={<TrainerSessionsPage />} />
              <Route path="notifications" element={<AdminNotificationsPage />} />
              <Route path="customers" element={<AdminCustomerUsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
            </Route>

            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/courses" replace />} />
              <Route path="courses" element={<AdminCoursesPage />} />
              <Route path="sessions" element={<AdminSessionsPage />} />
              <Route path="bookings" element={<AdminBookingsPage />} />
              <Route path="resources" element={<AdminResourcesPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="vouchers" element={<AdminVouchersPage />} />
              <Route path="discounts" element={<AdminDiscountsPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="notifications" element={<AdminNotificationsPage />} />
              <Route path="users/staff" element={<AdminStaffUsersPage />} />
              <Route path="customers" element={<AdminCustomerUsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="trainer/:trainerId" element={<TrainerDetailPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  )
}
